/**
 * AccessConfigDialog — per-form access configuration.
 *
 * Lets an org_admin set, per group and per role assigned to this app, a set of
 * default field values and a default record visibility — scoped to a single form.
 */
import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash, UsersThree, ShieldCheck, FloppyDisk } from "@phosphor-icons/react";

const VISIBILITIES = [
    { value: "__inherit__", label: "— Inherit (use form default) —" },
    { value: "creator_only", label: "Creator only" },
    { value: "creator_or_admin", label: "Creator + Admin" },
    { value: "group_only", label: "Group only" },
    { value: "admin_only", label: "Admin only" },
    { value: "everyone", label: "Everyone in org" },
];

export function AccessConfigDialog({ formId, open, onOpenChange, fields, accessGroupIds, accessRoleIds }) {
    const [groups, setGroups] = useState([]);
    const [roles, setRoles] = useState([]);
    const [configs, setConfigs] = useState([]); // [{ subject_type, subject_id, default_values:{}, default_record_visibility }]
    const [activeKey, setActiveKey] = useState(null); // `${subject_type}:${subject_id}`
    const [draftField, setDraftField] = useState("");
    const [draftValue, setDraftValue] = useState("");
    const [saving, setSaving] = useState(false);

    const defaultableFields = useMemo(
        () => (fields || []).filter((f) => f.default_group_value_enabled),
        [fields],
    );

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const [g, r, c] = await Promise.all([
                    api.get("/groups"), api.get("/roles"),
                    api.get(`/forms/${formId}/access-configs`),
                ]);
                setGroups(g.data); setRoles(r.data); setConfigs(c.data || []);
                if (c.data?.length) setActiveKey(`${c.data[0].subject_type}:${c.data[0].subject_id}`);
            } catch (e) { toast.error(extractError(e)); }
        })();
    }, [open, formId]);

    // Subjects assigned to this form (via Share Form access)
    const assignedGroups = useMemo(
        () => groups.filter((g) => (accessGroupIds || []).includes(g.id)),
        [groups, accessGroupIds],
    );
    const assignedRoles = useMemo(
        () => roles.filter((r) => (accessRoleIds || []).includes(r.id) && !r.is_system),
        [roles, accessRoleIds],
    );

    const ensureConfig = (subject_type, subject_id) => {
        const key = `${subject_type}:${subject_id}`;
        const exists = configs.find((c) => `${c.subject_type}:${c.subject_id}` === key);
        if (!exists) {
            const next = [...configs, { subject_type, subject_id, default_values: {}, default_record_visibility: null }];
            setConfigs(next);
        }
        setActiveKey(key);
    };

    const activeConfig = useMemo(
        () => configs.find((c) => `${c.subject_type}:${c.subject_id}` === activeKey) || null,
        [configs, activeKey],
    );

    const updateActive = (patch) => {
        setConfigs((prev) => prev.map((c) =>
            `${c.subject_type}:${c.subject_id}` === activeKey ? { ...c, ...patch } : c,
        ));
    };

    const addDefault = () => {
        if (!draftField || !activeConfig) return;
        updateActive({ default_values: { ...(activeConfig.default_values || {}), [draftField]: draftValue } });
        setDraftField(""); setDraftValue("");
    };

    const removeDefault = (k) => {
        const dv = { ...(activeConfig.default_values || {}) };
        delete dv[k];
        updateActive({ default_values: dv });
    };

    const removeConfig = (key) => {
        setConfigs((prev) => prev.filter((c) => `${c.subject_type}:${c.subject_id}` !== key));
        if (activeKey === key) setActiveKey(null);
    };

    const save = async () => {
        setSaving(true);
        try {
            // Strip empty configs (no defaults AND no visibility override)
            const payload = configs
                .filter((c) => Object.keys(c.default_values || {}).length > 0 || c.default_record_visibility)
                .map((c) => ({
                    subject_type: c.subject_type, subject_id: c.subject_id,
                    default_values: c.default_values || {},
                    default_record_visibility: c.default_record_visibility || null,
                }));
            await api.put(`/forms/${formId}/access-configs`, { configs: payload });
            toast.success("Access defaults saved");
            onOpenChange(false);
        } catch (e) { toast.error(extractError(e)); }
        finally { setSaving(false); }
    };

    const subjectLabel = (c) => {
        if (c.subject_type === "group") return groups.find((g) => g.id === c.subject_id)?.name || "unknown group";
        return roles.find((r) => r.id === c.subject_id)?.name || "unknown role";
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0" data-testid="access-config-dialog">
                <DialogHeader className="px-6 pt-6">
                    <DialogTitle>App-level defaults</DialogTitle>
                    <DialogDescription>
                        Set default field values and record visibility per group / role — scoped to this form only.
                        Per-form configs override the legacy global group defaults.
                    </DialogDescription>
                </DialogHeader>

                {defaultableFields.length === 0 && (
                    <div className="mx-6 mb-2 mt-2 text-xs border border-warning/40 bg-warning/10 text-foreground p-3 rounded-sm" data-testid="no-defaultable-fields">
                        No fields on this form have <code className="font-mono">Group default</code> enabled. Edit a field and turn on "Group default" to allow per-group/role defaults.
                    </div>
                )}

                <div className="grid grid-cols-[260px_1fr] flex-1 min-h-0 border-t border-border">
                    {/* LEFT: groups/roles list */}
                    <aside className="border-r border-border overflow-y-auto" data-testid="access-config-sidebar">
                        <div className="px-3 py-2 border-b border-border bg-secondary/50 sticky top-0">
                            <div className="label-overline flex items-center gap-1"><UsersThree size={12} /> Groups</div>
                        </div>
                        {assignedGroups.length === 0 && (
                            <div className="text-xs text-muted-foreground p-3 leading-relaxed">
                                No groups have access to this form yet. Click <span className="font-semibold text-foreground">Share</span> in the header to grant a group access, then come back to set per-group defaults & record visibility here.
                            </div>
                        )}
                        {assignedGroups.map((g) => {
                            const key = `group:${g.id}`;
                            const has = configs.find((c) => `${c.subject_type}:${c.subject_id}` === key);
                            return (
                                <button key={g.id} type="button"
                                    onClick={() => ensureConfig("group", g.id)}
                                    className={`w-full text-left px-3 py-2 text-sm border-b border-border hover:bg-secondary/40 ${activeKey === key ? "bg-primary/10 border-l-2 border-l-primary" : ""}`}
                                    data-testid={`access-cfg-group-${g.id}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="truncate">{g.name}</span>
                                        {has && (
                                            <Badge variant="outline" className="rounded-sm font-mono text-[9px]">
                                                {Object.keys(has.default_values || {}).length}d{has.default_record_visibility ? "·v" : ""}
                                            </Badge>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                        <div className="px-3 py-2 border-b border-border bg-secondary/50 sticky top-0 mt-2">
                            <div className="label-overline flex items-center gap-1"><ShieldCheck size={12} /> Roles</div>
                        </div>
                        {assignedRoles.length === 0 && (
                            <div className="text-xs text-muted-foreground p-3 leading-relaxed">
                                No custom roles have access to this form. Grant a role via <span className="font-semibold text-foreground">Share</span> to configure role-based defaults here.
                            </div>
                        )}
                        {assignedRoles.map((r) => {
                            const key = `role:${r.id}`;
                            const has = configs.find((c) => `${c.subject_type}:${c.subject_id}` === key);
                            return (
                                <button key={r.id} type="button"
                                    onClick={() => ensureConfig("role", r.id)}
                                    className={`w-full text-left px-3 py-2 text-sm border-b border-border hover:bg-secondary/40 ${activeKey === key ? "bg-primary/10 border-l-2 border-l-primary" : ""}`}
                                    data-testid={`access-cfg-role-${r.id}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="truncate">{r.name}</span>
                                        {has && (
                                            <Badge variant="outline" className="rounded-sm font-mono text-[9px]">
                                                {Object.keys(has.default_values || {}).length}d{has.default_record_visibility ? "·v" : ""}
                                            </Badge>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </aside>

                    {/* RIGHT: editor for active config */}
                    <section className="overflow-y-auto p-6" data-testid="access-config-editor">
                        {!activeConfig ? (
                            <div className="text-center text-sm text-muted-foreground mt-10 max-w-sm mx-auto leading-relaxed">
                                {assignedGroups.length === 0 && assignedRoles.length === 0 ? (
                                    <>
                                        <div className="font-medium text-foreground mb-2">Nothing to configure yet</div>
                                        To set default field values or record visibility for a group/role, first share this form with them from the <span className="font-semibold text-foreground">Share</span> dialog. They'll show up in this sidebar automatically.
                                    </>
                                ) : (
                                    <>Pick a group or role on the left to configure defaults for this app.</>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="label-overline">{activeConfig.subject_type}</div>
                                        <h3 className="text-lg font-bold tracking-tight">{subjectLabel(activeConfig)}</h3>
                                    </div>
                                    <Button variant="ghost" size="sm" className="rounded-sm text-destructive" onClick={() => removeConfig(activeKey)} data-testid="clear-access-config">
                                        <Trash size={12} className="mr-1" /> Clear
                                    </Button>
                                </div>

                                <div>
                                    <Label className="label-overline">Default record visibility</Label>
                                    <Select
                                        value={activeConfig.default_record_visibility || "__inherit__"}
                                        onValueChange={(v) => updateActive({ default_record_visibility: v === "__inherit__" ? null : v })}
                                    >
                                        <SelectTrigger className="mt-2 rounded-sm" data-testid="access-visibility-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {VISIBILITIES.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div>
                                    <Label className="label-overline mb-2 block">Default field values</Label>
                                    <p className="text-xs text-muted-foreground mb-3">
                                        Only fields with <code className="font-mono">Group default</code> enabled accept defaults.
                                    </p>
                                    <div className="space-y-2 mb-3">
                                        {Object.entries(activeConfig.default_values || {}).map(([k, v]) => (
                                            <div key={k} className="flex items-center gap-2 border border-border rounded-sm p-2">
                                                <span className="font-mono text-xs">{k}</span>
                                                <span className="text-xs text-muted-foreground">=</span>
                                                <span className="text-sm flex-1">{String(v)}</span>
                                                <Button type="button" size="icon" variant="ghost" onClick={() => removeDefault(k)} data-testid={`access-remove-default-${k}`}>
                                                    <Trash size={12} />
                                                </Button>
                                            </div>
                                        ))}
                                        {Object.keys(activeConfig.default_values || {}).length === 0 && (
                                            <div className="text-xs text-muted-foreground italic">No defaults yet</div>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
                                        <Select value={draftField} onValueChange={setDraftField}>
                                            <SelectTrigger className="rounded-sm" data-testid="access-default-field-select"><SelectValue placeholder="field" /></SelectTrigger>
                                            <SelectContent>
                                                {defaultableFields.length === 0 && <SelectItem value="__none__" disabled>No defaultable fields</SelectItem>}
                                                {defaultableFields.map((f) => (
                                                    <SelectItem key={f.id} value={f.field_name}>{f.field_label} ({f.field_name})</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <Input placeholder="value" value={draftValue} onChange={(e) => setDraftValue(e.target.value)} className="rounded-sm" data-testid="access-default-val-input" />
                                        <Button type="button" onClick={addDefault} variant="outline" className="rounded-sm" data-testid="access-add-default-btn"><Plus size={12} className="mr-1" /> Add</Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>
                </div>

                <DialogFooter className="border-t border-border px-6 py-4">
                    <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
                    <Button onClick={save} disabled={saving} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="save-access-config-btn">
                        <FloppyDisk size={14} className="mr-1" /> {saving ? "Saving…" : "Save defaults"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
